import {
  EntityIntegrationLinkShortResponse,
  IntegrationInfoResponse,
  IntegrationLinksResponse,
  IntegrationProviderDTO,
} from '@/modules/common/types/dto/response/integration-response';

import { EntityIntegration, ExternalEntity } from '../types/entity-integration';
import { IntegrationServices } from '../types/integrations';
import { getIntegrationIcon } from '../utils/integration-icons';

/**
 * Maps API integration provider DTO to application integration service enum.
 * Handles both request and response DTOs since they use the same base type.
 */
export const mapIntegrationProviderFromDTO = (
  provider: IntegrationProviderDTO
): IntegrationServices => {
  switch (provider) {
    case IntegrationProviderDTO.GOOGLE:
      return IntegrationServices.GOOGLE;
    case IntegrationProviderDTO.JIRA:
      return IntegrationServices.JIRA;
    case IntegrationProviderDTO.QUICK_BOOKS:
      return IntegrationServices.QUICKBOOKS;
    case IntegrationProviderDTO.TEMPO:
      return IntegrationServices.TEMPO;
    case IntegrationProviderDTO.CLOCKIFY:
      return IntegrationServices.CLOCKIFY;
    case IntegrationProviderDTO.ZOHO_PEOPLE:
      return IntegrationServices.ZOHO_PEOPLE;
    case IntegrationProviderDTO.UNKNOWN:
    default:
      return IntegrationServices.GOOGLE; // Fallback to a valid service
  }
};

/**
 * Maps application integration service enum to API DTO.
 * Used when preparing data for API requests.
 */
export const mapIntegrationProviderToDTO = (
  service: IntegrationServices
): IntegrationProviderDTO => {
  switch (service) {
    case IntegrationServices.GOOGLE:
      return IntegrationProviderDTO.GOOGLE;
    case IntegrationServices.JIRA:
      return IntegrationProviderDTO.JIRA;
    case IntegrationServices.QUICKBOOKS:
      return IntegrationProviderDTO.QUICK_BOOKS;
    case IntegrationServices.TEMPO:
      return IntegrationProviderDTO.TEMPO;
    case IntegrationServices.CLOCKIFY:
      return IntegrationProviderDTO.CLOCKIFY;
    case IntegrationServices.SLACK:
    case IntegrationServices.NOTION:
    case IntegrationServices.BAMBOOHR:
    default:
      return IntegrationProviderDTO.UNKNOWN;
  }
};

/**
 * Maps an array of integration providers from DTO to application format.
 */
export const mapIntegrationProvidersFromDTO = (
  providers: IntegrationProviderDTO[]
): IntegrationServices[] => {
  return providers.map(mapIntegrationProviderFromDTO);
};

/**
 * Maps an array of integration services to DTO format.
 */
export const mapIntegrationProvidersToDTO = (
  services: IntegrationServices[]
): IntegrationProviderDTO[] => {
  return services.map(mapIntegrationProviderToDTO);
};

// ===== Entity Integration Link Mappers =====

/**
 * Maps external entity link from API DTO to domain model.
 */
export const mapExternalEntityFromDTO = (
  dto: EntityIntegrationLinkShortResponse
): ExternalEntity => {
  return {
    id: String(dto.id),
    name: dto.name,
  };
};

/**
 * Maps integration links response to EntityIntegration domain model.
 * Converts unlinked external entities for a single integration provider.
 */
export const mapIntegrationLinksFromDTO = (dto: IntegrationLinksResponse): EntityIntegration => {
  const integrationType = mapIntegrationProviderFromDTO(dto.integration);

  return {
    id: integrationType, // Use integration type as ID
    type: integrationType,
    icon: getIntegrationIcon(integrationType),
    entities: dto.links.map(mapExternalEntityFromDTO),
    selectedEntity: null, // Initially no selection
  };
};

/**
 * Maps array of integration links responses to EntityIntegration array.
 */
export const mapIntegrationLinksListFromDTO = (
  dtos: IntegrationLinksResponse[]
): EntityIntegration[] => {
  return dtos.map(mapIntegrationLinksFromDTO);
};

/**
 * Maps integration info from entity details response.
 * Returns the selected external entity if connected.
 */
export const mapSelectedIntegrationFromDTO = (
  dto: IntegrationInfoResponse
): { type: string; selectedEntity: ExternalEntity | null } => {
  const integrationType = mapIntegrationProviderFromDTO(dto.integrationProviderType);

  return {
    type: integrationType,
    selectedEntity: dto.connectedIntegration
      ? {
          id: String(dto.connectedIntegration.id),
          name: dto.connectedIntegration.name,
        }
      : null,
  };
};

/**
 * Merges unlinked integrations with selected integrations from entity details.
 * This ensures:
 * 1. All available external entities from /unlinked endpoint are shown
 * 2. Selected external entity from details is included in the entities list
 * 3. Selected external entity is properly set as selectedEntity
 *
 * @param unlinkedIntegrations - Integrations with all unlinked external entities
 * @param selectedIntegrations - Integrations with selected external entities from details
 * @returns Merged integrations with complete entity lists and selections
 */
export const mergeIntegrations = (
  unlinkedIntegrations: EntityIntegration[],
  selectedIntegrations: IntegrationInfoResponse[]
): EntityIntegration[] => {
  // First, merge unlinked integrations with selected values
  const mergedUnlinked = unlinkedIntegrations.map(unlinked => {
    // Find matching selected integration by type
    const selectedInfo = selectedIntegrations.find(
      sel => mapIntegrationProviderFromDTO(sel.integrationProviderType) === unlinked.type
    );

    if (!selectedInfo || !selectedInfo.connectedIntegration) {
      // No selection for this integration
      return unlinked;
    }

    const selected = mapSelectedIntegrationFromDTO(selectedInfo);

    // Check if selected entity is already in the unlinked entities list
    const isSelectedInList = unlinked.entities?.some(e => e.id === selected.selectedEntity?.id);

    // If selected entity is not in the list, add it to the beginning
    const mergedEntities = isSelectedInList
      ? unlinked.entities
      : selected.selectedEntity
        ? [selected.selectedEntity, ...(unlinked.entities || [])]
        : unlinked.entities;

    return {
      ...unlinked,
      entities: mergedEntities,
      selectedEntity: selected.selectedEntity,
    };
  });

  // Add integrations from selectedIntegrations that aren't in unlinkedIntegrations
  const missingIntegrations = selectedIntegrations.reduce<EntityIntegration[]>((acc, selected) => {
    const integrationType = mapIntegrationProviderFromDTO(selected.integrationProviderType);

    // Skip if this integration type already exists in unlinkedIntegrations
    if (unlinkedIntegrations.some(unlinked => unlinked.type === integrationType)) {
      return acc;
    }

    const selectedEntity = selected.connectedIntegration
      ? {
          id: String(selected.connectedIntegration.id),
          name: selected.connectedIntegration.name,
        }
      : null;

    acc.push({
      id: integrationType,
      type: integrationType,
      icon: getIntegrationIcon(integrationType),
      entities: selectedEntity ? [selectedEntity] : [],
      selectedEntity,
    });

    return acc;
  }, []);

  return [...mergedUnlinked, ...missingIntegrations];
};
